import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/DashboardLayout';
import ClientLayout from './components/ClientLayout';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminsPage from './pages/admin/AdminsPage';
import DistributorsPage from './pages/admin/DistributorsPage';
import ClientsPage from './pages/admin/ClientsPage';
import SupplementsPage from './pages/admin/SupplementsPage';
import OrdersPage from './pages/admin/OrdersPage';
import SupportPage from './pages/admin/SupportPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import HealthTestsPage from './pages/admin/HealthTestsPage';
import HealthResultsPage from './pages/admin/HealthResultsPage';
import AdminDietsPage from './pages/admin/AdminDietsPage';
import AdminPlatosPage from './pages/admin/AdminPlatosPage';
import AdminBebidasPage from './pages/admin/AdminBebidasPage';
import AdminIngredientesPage from './pages/admin/AdminIngredientesPage';
import AdminHealthDashboardPage from './pages/admin/AdminHealthDashboardPage';
import PrescriptionsPage from './pages/admin/PrescriptionsPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminMovementsPage from './pages/admin/AdminMovementsPage';
import AdminVirtualProductsPage from './pages/admin/AdminVirtualProductsPage';
import AdminGananciasPage from './pages/admin/AdminGananciasPage';
import AdminCapitalPage from './pages/admin/AdminCapitalPage';
import ClientHealthPage from './pages/client/ClientHealthPage';
import ClientPrescriptionsPage from './pages/client/ClientPrescriptionsPage';
import ClientAnalyticsPage from './pages/client/ClientAnalyticsPage';
import ClientUploadAnalyticsPage from './pages/client/ClientUploadAnalyticsPage';
import ClientAnalyticsHistoryPage from './pages/client/ClientAnalyticsHistoryPage';
import ClientUploadsListPage from './pages/client/ClientUploadsListPage';
import ClientDietPage from './pages/client/ClientDietPage';
import ClientNutriPlanPage from './pages/client/ClientNutriPlanPage';
import ClientSpecialistsPage from './pages/client/ClientSpecialistsPage';
import ClientHelpPage from './pages/client/ClientHelpPage';
import ClientContactPage from './pages/client/ClientContactPage';
import ClientMessagesPage from './pages/client/ClientMessagesPage';
import ClientConversationsListPage from './pages/client/ClientConversationsListPage';
import ClientSupplementsPage from './pages/client/ClientSupplementsPage';
import ClientRecommendedPage from './pages/client/ClientRecommendedPage';
import ClientStorePage from './pages/client/ClientStorePage';
import ClientOrdersPage from './pages/client/ClientOrdersPage';
import ClientPromotionsPage from './pages/client/ClientPromotionsPage';
import ClientServicesPage from './pages/client/ClientServicesPage';
import ClientDistributorPage from './pages/client/ClientDistributorPage';
import ClientNearbyPage from './pages/client/ClientNearbyPage';
import ClientEventsPage from './pages/client/ClientEventsPage';
import ClientGroupsPage from './pages/client/ClientGroupsPage';
import ClientSubscriptionsPage from './pages/client/ClientSubscriptionsPage';

// Distributor Pages
import DistributorLayout from './components/DistributorLayout';
import DistributorDashboard from './pages/distributor/DistributorDashboard';
import DistributorClientsPage from './pages/distributor/DistributorClientsPage';
import DistributorFormsPage from './pages/distributor/DistributorFormsPage';
import DistributorSupplementsPage from './pages/distributor/DistributorSupplementsPage';
import DistributorCreateOrderPage from './pages/distributor/DistributorCreateOrderPage';
import DistributorProfilePage from './pages/distributor/DistributorProfilePage';
import DistributorSupportPage from './pages/distributor/DistributorSupportPage';
import DistributorHelpPage from './pages/distributor/DistributorHelpPage';
import DistributorContactPage from './pages/distributor/DistributorContactPage';
import DistributorMessagesPage from './pages/distributor/DistributorMessagesPage';
import DistributorChatPage from './pages/distributor/DistributorChatPage';
import DistributorClientProfilePage from './pages/distributor/DistributorClientProfilePage';
import DistClientFormsPage from './pages/distributor/DistClientFormsPage';
import DistClientTestDetailPage from './pages/distributor/DistClientTestDetailPage';
import DistClientPrescriptionsPage from './pages/distributor/DistClientPrescriptionsPage';
import DistClientBloodTestsPage from './pages/distributor/DistClientBloodTestsPage';
import DistributorDietConfiguratorPage from './pages/distributor/DistributorDietConfiguratorPage';
import DistributorSavedDietsPage from './pages/distributor/DistributorSavedDietsPage';
import DistributorDietPreviewPage from './pages/distributor/DistributorDietPreviewPage';

// Admin Pages (additional)
import AdminMessagesPage from './pages/admin/AdminMessagesPage';

// Client Pages
import ClientDashboard from './pages/client/ClientDashboard';
import DietConfiguratorPage from './pages/client/DietConfiguratorPage';
import SavedDietsPage from './pages/client/SavedDietsPage';

// Public Pages
import ClientRegistrationPage from './pages/ClientRegistrationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SharedFormPage from './pages/SharedFormPage';

// Shared Pages
import SupplementsViewPage from './pages/shared/SupplementsViewPage';
import OrdersViewPage from './pages/shared/OrdersViewPage';
import UserSupportPage from './pages/shared/UserSupportPage';
import HealthTestListPage from './pages/shared/HealthTestListPage';
import DynamicHealthSurvey from './pages/shared/DynamicHealthSurvey';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(user?.user_type)) {
    return <Navigate to={`/${user?.user_type}`} replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  if (isAuthenticated && user) {
    return <Navigate to={`/${user.user_type}`} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Route - Login */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />

      {/* Public Route - Client Registration via Distributor Link */}
      <Route path="/register/:token" element={<ClientRegistrationPage />} />

      {/* Public Route - Password Reset */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Public Route - Shared Health Form */}
      <Route path="/health-form/:token/:formId" element={<SharedFormPage />} />

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedTypes={['admin']}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="admins" element={<AdminsPage />} />
        <Route path="distributors" element={<DistributorsPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="supplements" element={<SupplementsPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="support-clients" element={<SupportPage userType="client" />} />
        <Route path="support-distributors" element={<SupportPage userType="distributor" />} />
        <Route path="notifications" element={<NotificationsPage isAdmin={true} />} />
        <Route path="health-tests" element={<AdminHealthDashboardPage />} />
        <Route path="health-tests/templates" element={<HealthTestListPage basePath="/admin" backPath="/admin/health-tests" backLabel="Herramientas de Salud" />} />
        <Route path="health-tests/results" element={<HealthResultsPage />} />
        <Route path="prescriptions" element={<PrescriptionsPage />} />
        <Route path="diets" element={<AdminDietsPage />} />
        <Route path="diets/platos" element={<AdminPlatosPage />} />
        <Route path="diets/bebidas" element={<AdminBebidasPage />} />
        <Route path="diets/ingredientes" element={<AdminIngredientesPage />} />
        <Route path="messages" element={<AdminMessagesPage />} />
        <Route path="operations/products" element={<AdminProductsPage />} />
        <Route path="operations/orders" element={<AdminOrdersPage />} />
        <Route path="operations/movements" element={<AdminMovementsPage />} />
        <Route path="operations/virtual-products" element={<AdminVirtualProductsPage />} />
        <Route path="finance/ganancias" element={<AdminGananciasPage />} />
        <Route path="finance/capital" element={<AdminCapitalPage />} />
      </Route>

      {/* Distributor Routes */}
      <Route 
        path="/distributor" 
        element={
          <ProtectedRoute allowedTypes={['distributor']}>
            <DistributorLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DistributorDashboard />} />
        <Route path="clients" element={<DistributorClientsPage />} />
        <Route path="clients/:clientId" element={<DistributorClientProfilePage />} />
        <Route path="clients/:clientId/forms" element={<DistClientFormsPage />} />
        <Route path="clients/:clientId/forms/:testId" element={<DistClientTestDetailPage />} />
        <Route path="clients/:clientId/prescriptions" element={<DistClientPrescriptionsPage />} />
        <Route path="clients/:clientId/blood-tests" element={<DistClientBloodTestsPage />} />
        <Route path="forms" element={<DistributorFormsPage />} />
        <Route path="profile" element={<DistributorProfilePage />} />
        <Route path="profile/forms" element={<DistributorFormsPage />} />
        <Route path="profile/diet-configurator" element={<DistributorDietConfiguratorPage />} />
        <Route path="profile/diets" element={<DistributorSavedDietsPage />} />
        <Route path="profile/diets/preview/:dietId" element={<DistributorDietPreviewPage />} />
        <Route path="supplements" element={<DistributorSupplementsPage />} />
        <Route path="supplements/create-order" element={<DistributorCreateOrderPage />} />
        <Route path="help" element={<DistributorHelpPage />} />
        <Route path="support" element={<DistributorSupportPage />} />
        <Route path="contact" element={<DistributorContactPage />} />
        <Route path="messages" element={<DistributorMessagesPage />} />
        <Route path="messages/:conversationId" element={<DistributorChatPage />} />
        <Route path="notifications" element={<NotificationsPage isAdmin={false} />} />
      </Route>

      {/* Client Routes */}
      <Route 
        path="/client" 
        element={
          <ProtectedRoute allowedTypes={['client']}>
            <ClientLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ClientHealthPage />} />
        <Route path="health-tests" element={<ClientHealthPage />} />
        <Route path="health-tests/results" element={<HealthResultsPage />} />
        <Route path="dashboard" element={<ClientDashboard />} />
        <Route path="prescriptions" element={<ClientPrescriptionsPage />} />
        <Route path="analytics" element={<ClientAnalyticsPage />} />
        <Route path="analytics/upload" element={<ClientUploadAnalyticsPage />} />
        <Route path="analytics/history" element={<ClientAnalyticsHistoryPage />} />
        <Route path="analytics/uploads" element={<ClientUploadsListPage />} />
        <Route path="diet" element={<ClientDietPage />} />
        <Route path="diet/configurator" element={<DietConfiguratorPage />} />
        <Route path="diet/saved" element={<SavedDietsPage />} />
        <Route path="diet/nutri-plan" element={<ClientNutriPlanPage />} />
        <Route path="health-tests-list" element={<HealthTestListPage basePath="/client" backPath="/client" backLabel="Mi Cuerpo" />} />
        <Route path="health-survey/:templateId" element={<DynamicHealthSurvey backPath="/client/health-tests-list" backLabel="Test de Salud" />} />
        <Route path="supplements" element={<ClientSupplementsPage />} />
        <Route path="supplements/recommended" element={<ClientRecommendedPage />} />
        <Route path="supplements/store" element={<ClientStorePage />} />
        <Route path="supplements/orders" element={<ClientOrdersPage />} />
        <Route path="supplements/promotions" element={<ClientPromotionsPage />} />
        <Route path="orders" element={<OrdersViewPage canUpdateStatus={false} />} />
        <Route path="support" element={<UserSupportPage />} />
        <Route path="specialists" element={<ClientSpecialistsPage />} />
        <Route path="services" element={<ClientServicesPage />} />
        <Route path="services/specialists" element={<ClientSpecialistsPage />} />
        <Route path="services/nearby" element={<ClientNearbyPage />} />
        <Route path="services/events" element={<ClientEventsPage />} />
        <Route path="services/groups" element={<ClientGroupsPage />} />
        <Route path="services/subscriptions" element={<ClientSubscriptionsPage />} />
        <Route path="help" element={<ClientHelpPage />} />
        <Route path="help/distributor" element={<ClientDistributorPage />} />
        <Route path="contact" element={<ClientContactPage />} />
        <Route path="messages" element={<ClientConversationsListPage />} />
        <Route path="messages/:conversationId" element={<ClientMessagesPage />} />
        <Route path="notifications" element={<NotificationsPage isAdmin={false} />} />
      </Route>

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                fontFamily: 'DM Sans, sans-serif',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
